import React, { useState, useEffect, useCallback } from 'react';
import { validateModalForm, sanitizeInput } from '../utils/validation';
import { useRateLimit, useHoneypot } from '../hooks/useRateLimit';

const Modal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    telegram: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | loading | success | error
  const [submitError, setSubmitError] = useState('');

  // Rate limiting
  const { blocked, blockTimeRemaining, checkLimit } = useRateLimit({
    maxAttempts: 5,
    windowMs: 60000,
    blockDurationMs: 60000,
  });

  // Honeypot
  const { isBot, HoneypotField } = useHoneypot();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', phone: '', telegram: '', message: '' });
      setErrors({});
      setTouched({});
      setSubmitStatus('idle');
      setSubmitError('');
    }
  }, [isOpen]);

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      let digits = value.replace(/\D/g, '');
      if (digits.length === 0) {
        setFormData((prev) => ({ ...prev, phone: '' }));
      } else {
        if (digits[0] === '7' || digits[0] === '8') {
          digits = digits.slice(1);
        }
        digits = digits.slice(0, 10);
        let formatted = '+7';
        if (digits.length > 0) formatted += ' (' + digits.slice(0, 3);
        if (digits.length >= 3) formatted += ') ';
        if (digits.length > 3) formatted += digits.slice(3, 6);
        if (digits.length > 6) formatted += '-' + digits.slice(6, 8);
        if (digits.length > 8) formatted += '-' + digits.slice(8, 10);
        setFormData((prev) => ({ ...prev, phone: formatted }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizeInput(value),
      }));
    }

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    const validationErrors = validateModalForm({
      name: formData.name,
      phone: formData.phone,
    });
    if (validationErrors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validationErrors[name] }));
    }
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check honeypot
    if (isBot()) {
      setSubmitStatus('success');
      return;
    }

    // Check rate limit
    if (!checkLimit()) {
      setSubmitError(`Подождите ${blockTimeRemaining} сек.`);
      return;
    }

    // Validate
    const validationErrors = validateModalForm({
      name: formData.name,
      phone: formData.phone,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({ name: true, phone: true });
      return;
    }

    setSubmitStatus('loading');
    setSubmitError('');

    try {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;

      const submitData = {
        name: sanitizeInput(formData.name),
        phone: formData.phone.replace(/[^\d+]/g, ''),
        telegram: sanitizeInput(formData.telegram),
        message: sanitizeInput(formData.message),
        source: 'modal_consultation',
        timestamp: Date.now(),
        pageUrl: window.location.href,
        device: isMobile ? 'mobile' : 'desktop',
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
      };

      const response = await fetch('https://leads.deshiki.ru/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '1seller-leads-2026-secure-key',
        },
        body: JSON.stringify({
          name: submitData.name,
          phone: submitData.phone,
          messenger: submitData.telegram || undefined,
          comment: submitData.message || undefined,
          source: window.location.hostname,
          pageUrl: window.location.pathname,
          device: submitData.device === 'mobile' ? `Мобильное (${submitData.screenSize})` : `Десктоп (${submitData.screenSize})`,
          screenSize: `${window.screen.width}x${window.screen.height}`,
        }),
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      setSubmitStatus('success');

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitStatus('error');
      setSubmitError('Ошибка отправки. Попробуйте позже.');
      console.error('Submit error:', error);
    }
  };

  const getInputClassName = (fieldName) => {
    const baseClass = 'modal-input';
    const errorClass = touched[fieldName] && errors[fieldName] ? 'modal-input-error' : '';
    return `${baseClass} ${errorClass}`.trim();
  };

  if (!isOpen) return null;

  // Success state
  if (submitStatus === 'success') {
    return (
      <>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              transform: translateY(30px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}</style>
        <div
          className="modal-overlay"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.3s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '48px',
              maxWidth: '480px',
              width: '100%',
              position: 'relative',
              animation: 'slideUp 0.3s ease',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
            }}
          >
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'rgba(34, 197, 94, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg
                style={{ width: '32px', height: '32px', color: '#22c55e' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#0F0F1A',
              marginBottom: '8px',
            }}>Отправлено!</h3>
            <p style={{ fontSize: '16px', color: 'rgba(15, 15, 26, 0.6)' }}>
              Свяжемся с вами в ближайшее время
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .modal-input {
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          padding: 16px 20px;
          border: 2px solid #E5E5E5;
          border-radius: 12px;
          outline: none;
          transition: all 0.3s ease;
          width: 100%;
          box-sizing: border-box;
        }

        .modal-input:focus {
          border-color: #FF6B35;
          box-shadow: 0 0 0 4px rgba(255, 107, 53, 0.1);
        }

        .modal-input-error {
          border-color: #ef4444;
        }

        .modal-input-error:focus {
          border-color: #ef4444;
        }

        .modal-input::placeholder {
          color: #999;
        }

        @media (max-width: 768px) {
          .modal-content {
            padding: 32px 24px !important;
          }

          .modal-title {
            font-size: 24px !important;
          }

          .modal-subtitle {
            font-size: 14px !important;
          }
        }
      `}</style>

      <div
        className="modal-overlay"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          animation: 'fadeIn 0.3s ease',
        }}
      >
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '500px',
            width: '100%',
            position: 'relative',
            animation: 'slideUp 0.3s ease',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#999',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f5f5f5';
              e.target.style.color = '#333';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#999';
              e.target.style.transform = 'rotate(0deg)';
            }}
          >
            ×
          </button>

          <h2 style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '32px',
            fontWeight: 900,
            color: '#0F0F1A',
            margin: '0 0 12px 0',
            lineHeight: 1.2,
          }} className="modal-title">
            Получить консультацию
          </h2>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '16px',
            color: 'rgba(0, 0, 0, 0.6)',
            margin: '0 0 32px 0',
            lineHeight: 1.5,
          }} className="modal-subtitle">
            Оставьте ваши контакты и мы свяжемся с вами в ближайшее время
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Honeypot */}
            <HoneypotField />

            {/* Error messages */}
            {blocked && (
              <div style={{
                padding: '12px 16px',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '12px',
                color: '#92400e',
                fontSize: '14px',
              }}>
                Подождите {blockTimeRemaining} сек.
              </div>
            )}
            {submitError && (
              <div style={{
                padding: '12px 16px',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '12px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                {submitError}
              </div>
            )}

            {/* Name */}
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ваше имя *"
                maxLength={50}
                autoComplete="name"
                className={getInputClassName('name')}
              />
              {touched.name && errors.name && (
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#ef4444' }}>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Телефон *"
                maxLength={20}
                autoComplete="tel"
                className={getInputClassName('phone')}
              />
              {touched.phone && errors.phone && (
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#ef4444' }}>
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Telegram */}
            <div>
              <input
                type="text"
                name="telegram"
                value={formData.telegram}
                onChange={handleChange}
                placeholder="Telegram (необязательно)"
                maxLength={50}
                className={getInputClassName('telegram')}
              />
            </div>

            {/* Message */}
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Ваш вопрос (необязательно)"
                rows={3}
                maxLength={1000}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '16px',
                  padding: '16px 20px',
                  border: '2px solid #E5E5E5',
                  borderRadius: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  resize: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FF6B35';
                  e.target.style.boxShadow = '0 0 0 4px rgba(255, 107, 53, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E5E5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitStatus === 'loading' || blocked}
              style={{
                background: submitStatus === 'loading' || blocked
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #FF6B35, #FF2D7C)',
                color: 'white',
                fontFamily: "'Inter', sans-serif",
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '18px 32px',
                border: 'none',
                borderRadius: '12px',
                cursor: submitStatus === 'loading' || blocked ? 'not-allowed' : 'pointer',
                marginTop: '8px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                if (submitStatus !== 'loading' && !blocked) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(255, 45, 124, 0.35)';
                  e.target.style.filter = 'brightness(1.08)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
                e.target.style.filter = 'brightness(1)';
              }}
            >
              {submitStatus === 'loading' ? (
                <>
                  <svg
                    style={{
                      animation: 'spin 1s linear infinite',
                      height: '20px',
                      width: '20px',
                    }}
                    viewBox="0 0 24 24"
                  >
                    <circle
                      style={{ opacity: 0.25 }}
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      style={{ opacity: 0.75 }}
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Отправка...
                </>
              ) : (
                'Отправить заявку'
              )}
            </button>

            {/* Privacy note */}
            <p style={{
              fontSize: '12px',
              color: 'rgba(15, 15, 26, 0.4)',
              textAlign: 'center',
              margin: '8px 0 0 0',
            }}>
              Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

export default Modal;
